import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { createProduct } from '@/services/productRegistry.service';
import {
  useProductRegistry,
  PRODUCT_CATEGORIES,
  type RegistryProduct,
  type ProductCategory,
} from '@/data/ProductRegistryContext';

interface QuickRegisterProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: { name?: string; sku?: string };
  onRegistered: (product: RegistryProduct) => void;
}

function generateSku(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 20);
}

export function QuickRegisterProductModal({
  open,
  onOpenChange,
  prefill,
  onRegistered,
}: QuickRegisterProductModalProps) {
  const { refreshProducts } = useProductRegistry();
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState<ProductCategory | ''>('');
  const [vendor, setVendor] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('each');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(prefill?.name ?? '');
      setSku(prefill?.sku ?? '');
      setCategory('');
      setVendor('');
      setUnitCost('');
      setUnitOfMeasure('each');
    }
  }, [open, prefill?.name, prefill?.sku]);

  const effectiveSku = sku.trim() || generateSku(name);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !category || !effectiveSku) return;

    const product: RegistryProduct = {
      productId: crypto.randomUUID(),
      productName: name.trim(),
      sku: effectiveSku,
      vendor: vendor.trim(),
      category: category as ProductCategory,
      subcategory: '',
      unitOfMeasure: unitOfMeasure.trim() || 'each',
      cost: parseFloat(unitCost) || 0,
      price: 0,
      trackInventory: false,
      stockQuantity: 0,
      reorderThreshold: 0,
      active: true,
      notes: '',
    };

    setSaving(true);
    try {
      await createProduct(product);
      await refreshProducts();
      onRegistered(product);
      onOpenChange(false);
      toast.success(`Product registered: ${product.productName}`);
    } catch (err: unknown) {
      const isDuplicate = (err as { code?: string })?.code === '23505';
      toast.error(
        isDuplicate
          ? 'A product with this SKU already exists — try a different SKU'
          : 'Failed to register product — please try again',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Register Product</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Product Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Wall Panel System Standard"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">SKU *</Label>
            <Input
              value={sku}
              onChange={(e) => setSku(e.target.value.trim().toUpperCase())}
              placeholder={name ? generateSku(name) : 'WALL-PANEL-STANDARD'}
              className="font-mono text-xs"
            />
            {!sku.trim() && name.trim() && (
              <p className="text-[10px] text-muted-foreground">
                Auto-generated: <span className="font-mono">{generateSku(name)}</span>
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Category *</Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as ProductCategory)}
              required
            >
              <SelectTrigger className="text-xs">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-xs">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Vendor</Label>
              <Input
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="Vendor name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Unit of Measure</Label>
              <Input
                value={unitOfMeasure}
                onChange={(e) => setUnitOfMeasure(e.target.value)}
                placeholder="each"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Unit Cost ($)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim() || !category}>
              {saving ? 'Registering…' : 'Register Product'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
